import React, { useCallback, useEffect, useState } from "react";
import apiClient from "../api/apiClient";
import { Product } from "../types/products.types";
import ProductDeleteModal from "./ProductDeleteModal";
import ProductEditingForm from "./ProductsEditForm";

interface ProductsApiResponse {
  data: Product[];
  page: number;
  limit: number;
  totalPages: number;
  totalWines: number;
}

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const fetchProducts = useCallback(
    async (page: number, search: string = "") => {
      setIsLoading(true);
      setError(null);
      // Do not clear notification here
      try {
        const params: { page: number; limit: number; search?: string } = {
          page,
          limit: 10,
        };
        if (search) {
          params.search = search;
        }
        const response = await apiClient.get<ProductsApiResponse>(
          "/api/products",
          {
            params,
          }
        );
        const data = response.data;
        setProducts(data.data || []);
        setCurrentPage(data.page || 1);
        setTotalPages(data.totalPages || 1);
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.error || err.message || "Failed to fetch wines.";
        setError(errorMessage);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    // Debounce search or fetch on explicit action
    // For now, fetch when currentPage or searchTerm changes (after a delay for typing)
    const handler = setTimeout(() => {
      fetchProducts(currentPage, searchTerm);
    }, 500); // Debounce search term by 500ms

    return () => {
      clearTimeout(handler);
    };
  }, [currentPage, searchTerm, fetchProducts]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setNotification(null);
  };

  const handleDelete = (pr: Product) => {
    setDeletingProduct(pr);
    setNotification(null);
  };

  const handleSaveEdit = async (updatedWineData: Partial<Product>) => {
    if (!editingProduct) return;
    setNotification("Salvataggio...");
    setIsLoading(true); // Indicate loading during save
    try {
      await apiClient.put(
        `/api/products/${editingProduct.id}`,
        updatedWineData
      );
      setNotification("Prodotto aggiornato con successo!");
      setEditingProduct(null);
      fetchProducts(currentPage, searchTerm); // Refetch with current search term
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to update wine.";
      setNotification(`Errore durante l'aggiornamento: ${errorMessage}`);
    } finally {
      setIsLoading(false); // Stop loading indicator
    }
  };

  const handleDeleteModal = async (product: number) => {
    if (!deletingProduct) return;
    setNotification("Cancellazione...");
    setIsLoading(true); // Indicate loading during save
    try {
      await apiClient.delete(`/api/products/${product}`);
      setNotification("Prodotto eliminato!");
      setDeletingProduct(null);
      fetchProducts(currentPage, searchTerm);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to update product.";
      setNotification(`Errore durante l'aggiornamento: ${errorMessage}`);
    } finally {
      setIsLoading(false); // Stop loading indicator
    }
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setDeletingProduct(null);
    setNotification(null);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage > 1) {
      pages.push(
        <button
          key="prev"
          onClick={() => {
            setNotification(null);
            setCurrentPage(currentPage - 1);
          }}
          disabled={isLoading}
        >
          Precedente
        </button>
      );
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => {
            setNotification(null);
            setCurrentPage(i);
          }}
          disabled={isLoading || i === currentPage}
          style={i === currentPage ? { fontWeight: "bold" } : {}}
        >
          {i}
        </button>
      );
    }
    if (currentPage < totalPages) {
      pages.push(
        <button
          key="next"
          onClick={() => {
            setNotification(null);
            setCurrentPage(currentPage + 1);
          }}
          disabled={isLoading}
        >
          Successivo
        </button>
      );
    }
    return <div style={{ marginTop: "10px" }}>{pages}</div>;
  };

  return (
    <div
      style={{
        margin: "20px",
        padding: "20px",
        border: "1px solid #ccc",
        borderRadius: "8px",
      }}
    >
      <h2>Gestione Prodotti</h2>
      <div style={{ marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="Cerca per nome..."
          value={searchTerm}
          onChange={handleSearchChange}
          style={{
            padding: "8px",
            width: "300px",
            marginRight: "10px",
            backgroundColor: "white",
          }}
        />
      </div>

      {isLoading && !editingProduct && <p>Caricamento prodotti...</p>}
      {error && <p style={{ color: "red" }}>Errore: {error}</p>}
      {notification && (
        <p
          style={{
            color: notification.startsWith("Errore") ? "red" : "green",
            marginTop: "10px",
          }}
        >
          {notification}
        </p>
      )}

      {!editingProduct && !isLoading && !error && products.length === 0 && (
        <p>Nessun prodotto trovato per "{searchTerm}".</p>
      )}

      {!editingProduct && !isLoading && !error && products.length > 0 && (
        <>
          <table
            style={{
              width: "100%",
              marginTop: "20px",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th style={tableHeaderStyle}>ID Prodotto</th>
                <th style={tableHeaderStyle}>Nome</th>
                <th style={tableHeaderStyle}>Descrizione (LLM)</th>
                <th style={tableHeaderStyle}>Abbinamenti (LLM)</th>
                <th style={tableHeaderStyle}>Categoria 1</th>
                <th style={tableHeaderStyle}>Categoria 2</th>
                <th style={tableHeaderStyle}>Categoria 3</th>
                <th style={tableHeaderStyle}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td style={tableCellStyle}>{product.id}</td>
                  <td style={tableCellStyle}>{product.name}</td>
                  <td style={tableCellStyle}>
                    <div style={cellContentStyle}>
                      {product.description || "N/A"}
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    <div style={cellContentStyle}>
                      {product.pairings || "N/A"}
                    </div>
                  </td>
                  <td style={tableCellStyle}>
                    {`${product?.category_1?.name || "N/A"}\r ID:${
                      product?.category_1?.id || "N/A"
                    }`}
                  </td>
                  <td style={tableCellStyle}>
                    {`${product?.category_2?.name || "N/A"}\n ID:${
                      product?.category_2?.id || "N/A"
                    }`}
                  </td>
                  <td style={tableCellStyle}>
                    {`${product?.category_3?.name || "N/A"}\n ID:${
                      product?.category_3?.id || "N/A"
                    }`}
                  </td>

                  <td style={tableCellStyleAction}>
                    <button
                      onClick={() => handleEdit(product)}
                      disabled={isLoading}
                    >
                      Modifica
                    </button>
                    <button
                      onClick={() => handleDelete(product)}
                      disabled={isLoading}
                      style={{ marginTop: "10px" }}
                    >
                      Elimina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {renderPagination()}
        </>
      )}
      {editingProduct && (
        <ProductEditingForm
          product={editingProduct}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
        />
      )}
      {deletingProduct && (
        <ProductDeleteModal
          product={deletingProduct}
          onDelete={handleDeleteModal}
          onCancel={handleCancelEdit}
        />
      )}
    </div>
  );
};

const tableHeaderStyle: React.CSSProperties = {
  border: "1px solid #ddd",
  padding: "6px",
  textAlign: "left",
  backgroundColor: "#f2f2f2",
};
const tableCellStyle: React.CSSProperties = {
  border: "1px solid #ddd",
  padding: "8px",
  textAlign: "left",
  verticalAlign: "top",
};
const tableCellStyleAction: React.CSSProperties = {
  border: "1px solid #ddd",
  padding: "8px",
  textAlign: "center",
  verticalAlign: "center",
};
const cellContentStyle: React.CSSProperties = {
  maxHeight: "100px",
  overflowY: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

export default ProductManagement;
